import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { companiesService, type Company } from '@/features/companies';
import { Button, Checkbox, Input } from '@/design-system';
import { cn } from '@/design-system/utils/cn';

const PAGE_SIZE = 50;

type CompanyMultiSelectProps = {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
};

export function CompanyMultiSelect({ selectedIds, onChange, disabled }: CompanyMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: queryKeys.companies.pickerList({
      limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
    }),
    queryFn: ({ pageParam }) =>
      companiesService.list({
        limit: PAGE_SIZE,
        page: pageParam,
        search: debouncedSearch || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const { page, totalPages } = last.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 60_000,
  });

  const companies: Company[] = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data?.pages]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (id: string) => {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const toggleAllVisible = () => {
    if (disabled) return;
    const ids = companies.map((c) => c.id);
    const allSelected = ids.every((id) => selectedSet.has(id));
    if (allSelected) {
      const drop = new Set(ids);
      onChange(selectedIds.filter((id) => !drop.has(id)));
    } else {
      onChange([...new Set([...selectedIds, ...ids])]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Input
          placeholder="Buscar empresas…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
        <Button variant="ghost" size="sm"
          type="button"
          className="text-primary whitespace-nowrap shrink-0"
          onClick={toggleAllVisible}
          disabled={disabled || companies.length === 0}
        >
          {companies.every((c) => selectedSet.has(c.id)) && companies.length > 0
            ? 'Limpar visíveis'
            : 'Selecionar visíveis'}
        </Button>
      </div>
      <p className="text-xs text-gray-500">{selectedIds.length} selecionada(s)</p>
      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {isLoading && <p className="p-3 text-sm text-gray-500">Carregando empresas…</p>}
        {!isLoading && companies.length === 0 && (
          <p className="p-3 text-sm text-gray-500">Nenhuma empresa encontrada.</p>
        )}
        {companies.map((company) => {
          const checked = selectedSet.has(company.id);
          return (
            <div
              key={company.id}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50',
                disabled && 'opacity-60 cursor-not-allowed'
              )}
            >
              <Checkbox
                label={company.fantasyName ? `${company.name} — ${company.fantasyName}` : company.name}
                checked={checked}
                onChange={() => toggle(company.id)}
                disabled={disabled}
              />
            </div>
          );
        })}
        {hasNextPage && (
          <Button variant="ghost" size="sm"
            type="button"
            className="w-full text-primary hover:bg-gray-50"
            onClick={() => void fetchNextPage()}
            disabled={disabled || isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </Button>
        )}
      </div>
    </div>
  );
}
